import { toast } from "sonner";
import {
  Phone,
  MessageSquare,
  Check,
  Flame,
  AlertCircle,
  Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  formatDate,
  getInitials,
  priorityReason,
  statutLabel,
  statutColor,
} from "@/lib/crmHelpers";

const PriorityRow = ({ lead, onOpenLead, onAction, onStatutChange }) => {
  const reasons = priorityReason(lead);
  const hot = lead.priorite === "haute";

  const handleCall = async (e) => {
    e.stopPropagation();
    await onAction(lead.id, "appel", `Appel vers ${lead.telephone}`);
    toast.success(`Appel enregistré : ${lead.nom}`);
    window.location.href = `tel:${lead.telephone.replace(/\s+/g, "")}`;
  };

  const handleSMS = async (e) => {
    e.stopPropagation();
    await onAction(lead.id, "sms", `SMS envoyé à ${lead.telephone}`);
    toast.success(`SMS enregistré : ${lead.nom}`);
    window.location.href = `sms:${lead.telephone.replace(/\s+/g, "")}`;
  };

  const handleDone = async (e) => {
    e.stopPropagation();
    await onAction(lead.id, "note", "Marqué comme traité");
    toast.success(`Traité : ${lead.nom}`);
  };

  return (
    <div
      data-testid={`priority-row-${lead.id}`}
      onClick={() => onOpenLead(lead)}
      className={`group relative flex cursor-pointer flex-col gap-3 rounded-xl border bg-card p-4 transition-all duration-200 hover:border-foreground/20 hover:shadow-sm md:flex-row md:items-center md:justify-between md:p-5 ${
        hot
          ? "border-red-200 dark:border-red-900/40"
          : "border-border/60"
      }`}
    >
      {hot && (
        <span
          className="pulse-hot absolute left-0 top-0 h-full w-[3px] rounded-l-xl bg-red-500"
          aria-hidden
        />
      )}

      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-muted font-heading text-sm font-semibold text-foreground">
          {getInitials(lead.nom)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-heading truncate font-semibold">
              {lead.nom}
            </span>
            <Badge
              variant="secondary"
              className={`border-0 text-[10px] font-medium uppercase tracking-wider ${statutColor(
                lead.statut
              )}`}
            >
              {statutLabel(lead.statut)}
            </Badge>
          </div>
          <div className="mt-0.5 text-sm text-muted-foreground">
            {lead.telephone}
            {lead.prochaine_action && (
              <span className="ml-2">
                · Relance {formatDate(lead.prochaine_action)}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {reasons.map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300"
              >
                {r.includes("retard") ? (
                  <AlertCircle className="h-3 w-3" />
                ) : (
                  <Flame className="h-3 w-3" />
                )}
                {r}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleCall}
          data-testid={`priority-call-${lead.id}`}
          className="gap-1.5"
        >
          <Phone className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Appeler</span>
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleSMS}
          data-testid={`priority-sms-${lead.id}`}
          className="gap-1.5"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">SMS</span>
        </Button>
        <Button
          size="sm"
          onClick={handleDone}
          data-testid={`priority-done-${lead.id}`}
          className="gap-1.5"
        >
          <Check className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Traité</span>
        </Button>
      </div>
    </div>
  );
};

export default function PrioritiesSection({
  leads,
  onOpenLead,
  onAction,
  onStatutChange,
}) {
  return (
    <section data-testid="priorities-section">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="text-label text-muted-foreground">À faire maintenant</div>
          <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Flame className="h-6 w-6 text-red-500" />
            Priorités du jour
          </h2>
        </div>
        <div className="text-sm text-muted-foreground">
          <span className="font-heading font-semibold text-foreground">
            {leads.length}
          </span>{" "}
          à traiter
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 bg-muted/20 py-12 text-center">
          <Inbox className="h-8 w-8 text-muted-foreground" />
          <div className="font-heading font-semibold">Tout est à jour !</div>
          <p className="max-w-md text-sm text-muted-foreground">
            Aucune priorité pour le moment. Ajoutez des leads ou planifiez des relances.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {leads.map((lead) => (
            <PriorityRow
              key={lead.id}
              lead={lead}
              onOpenLead={onOpenLead}
              onAction={onAction}
              onStatutChange={onStatutChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}
